if (!customElements.get('cart-recommendations-v2')) {
  customElements.define(
    'cart-recommendations-v2',
    class CartRecommendationsV2 extends HTMLElement {
      async connectedCallback() {
        const shouldReload =
          this.dataset.crReload === 'true' || this.children.length === 0;

        if (this.dataset.crReload === 'true') {
          delete this.dataset.crReload;
        }

        // Keep carried-over cards interactive while a seed-change reload runs.
        if (this.children.length) this.init();

        if (shouldReload) {
          await this.load();
          this.init();
        }
      }

      async load() {
        const { productId, url } = this.dataset;
        if (!productId || !url || this._loading) return;

        this._loading = true;
        try {
          const response = await fetch(`${url}&product_id=${productId}`);
          // Shopify's recommendations endpoint is briefly unavailable or throttled at times: the
          // drawer just shows no recommendations, nothing to report.
          if (response.status >= 500 || response.status === 429) return;
          // 404: the seed product (first cart line) isn't published to this storefront, so it has
          // no recommendations. Same outcome as an empty result.
          if (response.status === 404) {
            this.replaceChildren();
            return;
          }
          if (!response.ok) throw new Error(`load failed: ${response.status}`);

          const next = new DOMParser()
            .parseFromString(await response.text(), 'text/html')
            .querySelector('cart-recommendations-v2');

          if (!next?.children.length) {
            this.replaceChildren();
            return;
          }

          this.replaceChildren(...next.childNodes);
        } catch (error) {
          console.warn('[cart-recommendations-v2] load failed', error);
          window.captureThemeError?.(error, 'cart-recommendations-v2', {
            action: 'load',
            productId,
          });
        } finally {
          this._loading = false;
        }
      }

      init() {
        this.scroller = this.querySelector('[data-cr-scroller]');
        if (!this.scroller) return;

        this.prevButton = this.querySelector('[data-cr-prev]');
        this.nextButton = this.querySelector('[data-cr-next]');

        this.update = this.update.bind(this);
        this.prevButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));
        this.scroller.addEventListener('scroll', this.update, { passive: true });

        this.querySelectorAll('[data-cr-card]').forEach((card) => this.initCard(card));
        this.update();
      }

      initCard(card) {
        const select = card.querySelector('[data-cr-variant]');
        const addButton = card.querySelector('[data-cr-add]');
        const image = card.querySelector('.cart-recommendations-v2__image');
        const prices = card.querySelector('[data-cr-prices]');
        const caption = card.querySelector('.star-rating__caption');

        if (caption) {
          const match = caption.textContent.match(/\d+/);
          if (match) caption.textContent = `(${match[0]} reviews)`;
        }

        select?.addEventListener('change', () => {
          const option = select.options[select.selectedIndex];
          if (!option) return;
          addButton.dataset.variantId = option.value;
          const { price, compare, image: variantImage } = option.dataset;
          if (prices && price) {
            prices.innerHTML =
              compare && compare !== price
                ? `<span class="cart-recommendations-v2__price-compare">${compare}</span><span class="cart-recommendations-v2__price">${price}</span>`
                : `<span class="cart-recommendations-v2__price">${price}</span>`;
          }
          if (image && variantImage) image.src = variantImage;
        });

        addButton?.addEventListener('click', () => this.add(addButton));
      }

      clearLoading(button) {
        if (!button) return;
        button.disabled = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }

      async add(button) {
        const variantId = button.dataset.variantId;
        if (!variantId || button.classList.contains('is-loading')) return;

        button.disabled = true;
        button.classList.add('is-loading');
        button.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
              sections: window.CART_SECTIONS,
              sections_url: window.cartSectionsContextUrl?.(),
            }),
          });
          const data = await response.json();
          if (!response.ok || data.status === 422) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cart-recommendations',
              errors: data.errors,
              message: data.description || data.message,
            });
            this.clearLoading(button);
            return;
          }

          // Clear loading before publish: cart-drawer re-renders and may clone
          // this markup via carryOverRecommendations. Leaving is-loading on the
          // live node freezes the spinner on the replacement button forever.
          this.clearLoading(button);

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            cartData: data,
            variantId,
          });
        } catch (error) {
          this.clearLoading(button);
          window.captureThemeError?.(error, 'cart-recommendations-v2', {
            action: 'add',
            variantId,
          });
        }
      }

      step(direction) {
        const item = this.scroller.firstElementChild;
        const distance = item ? item.offsetWidth : this.scroller.clientWidth;
        this.scroller.scrollBy({ left: distance * direction, behavior: 'smooth' });
      }

      update() {
        const max = this.scroller.scrollWidth - this.scroller.clientWidth;
        if (max <= 0) {
          this.prevButton.hidden = true;
          this.nextButton.hidden = true;
          return;
        }
        this.prevButton.hidden = this.scroller.scrollLeft <= 0;
        this.nextButton.hidden = this.scroller.scrollLeft >= max - 1;
      }
    }
  );
}
